import { LabelValue } from '../../types';

/**
 * Allowed characters for the label display name (trimmed input).
 * Matches the full string: Unicode letters and numbers, spaces, hyphen, underscore only.
 * Used by {@link validateDisplayName} against the trimmed value.
 */
const DISPLAY_NAME_PATTERN = /^[\p{L}\p{N}\s\-_]*$/u;

/**
 * Allowed characters for the optional description field.
 * Same as display name, plus comma and period.
 * Used by {@link validateDescription} when the description is non-empty.
 */
const DESCRIPTION_PATTERN = /^[\p{L}\p{N}\s\-_,.]*$/u;

/**
 * Allowed characters for a label value (trimmed input in the values list).
 * Same character set as {@link DISPLAY_NAME_PATTERN}.
 * Used by {@link validateNewValue} against the trimmed value.
 */
const VALUE_PATTERN = /^[\p{L}\p{N}\s\-_]*$/u;

const DISPLAY_NAME_MAX_LENGTH = 30;
const DESCRIPTION_MAX_LENGTH = 100;
const VALUE_MAX_LENGTH = 50;

export const LABEL_PANEL_VALIDATION = {
	DISPLAY_NAME_MAX_LENGTH,
	DESCRIPTION_MAX_LENGTH,
	VALUE_MAX_LENGTH
} as const;

const VALIDATION_ERRORS = {
	DISPLAY_NAME_REQUIRED: 'Display name is required',
	DISPLAY_NAME_TOO_LONG: `Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or less`,
	DISPLAY_NAME_INVALID:
		'Display name can only contain letters, numbers, spaces, hyphens, and underscores',
	DESCRIPTION_TOO_LONG: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`,
	DESCRIPTION_INVALID:
		'Description can only contain letters, numbers, spaces, hyphens, underscores, commas, and periods',
	VALUE_REQUIRED: 'Value is required',
	VALUE_TOO_LONG: `Value must be ${VALUE_MAX_LENGTH} characters or less`,
	VALUE_INVALID: 'Value can only contain letters, numbers, spaces, hyphens, and underscores',
	VALUE_DUPLICATE: 'This value already exists'
} as const;

export const validateDisplayName = (value: string): string | undefined => {
	const trimmed = value.trim();

	if (!trimmed) {
		return VALIDATION_ERRORS.DISPLAY_NAME_REQUIRED;
	}

	if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) {
		return VALIDATION_ERRORS.DISPLAY_NAME_TOO_LONG;
	}

	if (!DISPLAY_NAME_PATTERN.test(trimmed)) {
		return VALIDATION_ERRORS.DISPLAY_NAME_INVALID;
	}

	return undefined;
};

export const validateDescription = (value: string): string | undefined => {
	if (!value) {
		return undefined;
	}

	if (value.length > DESCRIPTION_MAX_LENGTH) {
		return VALIDATION_ERRORS.DESCRIPTION_TOO_LONG;
	}

	if (!DESCRIPTION_PATTERN.test(value)) {
		return VALIDATION_ERRORS.DESCRIPTION_INVALID;
	}

	return undefined;
};

export const validateNewValue = (
	value: string,
	existingValues: LabelValue[]
): string | undefined => {
	const trimmed = value.trim();

	if (!trimmed) {
		return VALIDATION_ERRORS.VALUE_REQUIRED;
	}

	if (trimmed.length > VALUE_MAX_LENGTH) {
		return VALIDATION_ERRORS.VALUE_TOO_LONG;
	}

	if (!VALUE_PATTERN.test(trimmed)) {
		return VALIDATION_ERRORS.VALUE_INVALID;
	}

	if (existingValues.some((v) => v.value === trimmed)) {
		return VALIDATION_ERRORS.VALUE_DUPLICATE;
	}

	return undefined;
};
